import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser, onAuthStateChanged, signOut, signInWithEmailAndPassword, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from '../firebase/config';
import { User, OSCData } from '../types';

interface UserAuthContextType {
  currentUser: User | null;
  loading: boolean;
  isUserSession: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutComplete: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const UserAuthContext = createContext<UserAuthContextType | undefined>(undefined);

export const useUserAuth = () => {
  const context = useContext(UserAuthContext);
  if (context === undefined) {
    throw new Error('useUserAuth must be used within a UserAuthProvider');
  }
  return context;
};

const fetchUserData = async (firebaseUser: FirebaseUser): Promise<User> => {
  try {
    const oscRef = ref(database, `oscs/${firebaseUser.uid}`);
    const snapshot = await get(oscRef);
    
    if (snapshot.exists()) {
      const oscData = snapshot.val();
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        oscData
      };
    } else {
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email || ''
      };
    }
  } catch (error) {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email || ''
    };
  }
};

export const UserAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUserSession, setIsUserSession] = useState(false);

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userData = await fetchUserData(userCredential.user);
    
    if (!userData.oscData) {
      // Não fazer signOut para não afetar outras sessões (admin)
      // await signOut(auth);
      throw new Error('Usuário não possui dados de OSC');
    }
    
    setCurrentUser(userData);
    setIsUserSession(true);
  };

  const logout = async () => {
    // Limpar estado local
    setCurrentUser(null);
    setIsUserSession(false);
    // Fazer signOut completo do Firebase para evitar re-login automático
    await signOut(auth);
  };

  const logoutComplete = async () => {
    // Limpar estado local
    setCurrentUser(null);
    setIsUserSession(false);
    // Fazer signOut completo do Firebase
    await signOut(auth);
  };

  const refreshUserData = async () => {
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      const userData = await fetchUserData(firebaseUser);
      // Se o usuário tem OSC, definir como sessão de usuário
      if (userData.oscData) {
        setCurrentUser(userData);
        setIsUserSession(true);
      } else {
        setCurrentUser(userData);
      }
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || !firebaseUser.email) {
      throw new Error('Usuário não autenticado');
    }

    try {
      // Reautenticar o usuário com a senha atual
      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      
      // Atualizar a senha
      await updatePassword(firebaseUser, newPassword);
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        throw new Error('Senha atual incorreta');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('A nova senha deve ter pelo menos 6 caracteres');
      } else {
        throw new Error('Erro ao alterar senha. Tente novamente.');
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      
      if (firebaseUser) {
        try {
          const userData = await fetchUserData(firebaseUser);
          
          // Só aceitar se for um usuário válido (tem oscData)
          if (userData.oscData) {
            setCurrentUser(userData);
            setIsUserSession(true);
          } else {
            // Não limpar a sessão se o usuário pode ser um admin
            // setCurrentUser(null);
            // setIsUserSession(false);
          }
        } catch (error) {
          setCurrentUser(null);
          setIsUserSession(false);
        }
      } else {
        setCurrentUser(null);
        setIsUserSession(false);
      }
      
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    loading,
    isUserSession,
    login,
    logout,
    logoutComplete,
    refreshUserData,
    changePassword
  };

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};